import { observer } from 'mobx-react-lite';
import { getSnapshot } from 'mobx-state-tree';
import React, { useState, useEffect } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Button,
  ControlGroup,
  Dialog,
  EmptyInputGroup,
  FormField,
  Grid,
  Heading,
  IDialogProps,
  InputGroup,
  majorScale,
  NumberInput,
  Spinner,
  TextArea,
  useToast,
} from '../../npaper';
import { IContactGroup, useStores } from '../../stores';
import { IProductGroup } from '../../stores/products/ProductGroupStore';
import { ContactGroupSelect } from '../groups/ContactGroupSelect';

export interface IProductGroupDetailsDialogProps
  extends Omit<IDialogProps, 'title' | 'actions' | 'onClose'> {
  productGroup?: IProductGroup;
  onClose?: (createdGroup?: IProductGroup) => void;
}

interface FormValues {
  name: string;
  description?: string | null;
  default_cmr: number;
  pricing?: {
    contact_group: number | string;
    cmr: number;
  }[];
}

const ProductGroupDetailsDialog: React.FC<IProductGroupDetailsDialogProps> = ({
  productGroup,
  onClose,
  isShown: defaultIsShown,
  ...dialogProps
}) => {
  const [isShown, setIsShown] = useState(defaultIsShown);
  const intl = useIntl();
  const toaster = useToast();

  const { productGroupStore } = useStores();

  const { reset, watch, formState, handleSubmit, errors, register, control } =
    useForm<FormValues>();

  const {
    fields: pricingMatrix,
    append,
    remove,
  } = useFieldArray({
    control, // control props comes from useForm (optional: if you are using FormContext)
    name: 'pricing', // unique name for your Field Array
    keyName: 'contact_group',
  });

  useEffect(() => {
    if (!isShown) return;
    if (productGroup) {
      const snapshot = getSnapshot(productGroup);
      reset({
        name: snapshot.name,
        description: snapshot.description,
        default_cmr: snapshot.default_cmr,
        pricing: snapshot.pricing,
      });
    } else {
      reset({
        default_cmr: 0.3,
      });
    }
  }, [productGroup, reset, isShown]);

  const onSubmit = async (values: FormValues) => {
    if (productGroup) {
      // update group
      try {
        await productGroupStore.updateItem(productGroup, values);
        close();
      } catch (err) {
        console.error(err);
        toaster.showDefaultError(err);
      }
    } else {
      // Create the group
      try {
        const newLocation = await productGroupStore.createItem({
          ...values,
        });
        close(newLocation);
      } catch (err) {
        console.error(err);
        toaster.showDefaultError(err);
      }
    }
  };

  useEffect(() => {
    setIsShown(defaultIsShown);
  }, [defaultIsShown]);

  const close = (createdGroup?: IProductGroup) => {
    setIsShown(false);
    onClose && onClose(createdGroup);
  };

  return (
    <Dialog
      {...dialogProps}
      title={
        productGroup
          ? intl.formatMessage({ defaultMessage: 'Edit group' })
          : intl.formatMessage({ defaultMessage: 'Create new group' })
      }
      isShown={isShown}
      onClose={() => {
        close();
      }}
      actions={
        <>
          <Button onClick={() => close()} disabled={formState.isSubmitting}>
            <FormattedMessage defaultMessage="Cancel" />
          </Button>
          <Button
            primary
            onClick={handleSubmit(onSubmit)}
            isLoading={formState.isSubmitting}
          >
            {productGroup ? (
              <FormattedMessage defaultMessage="Save changes" />
            ) : (
              <FormattedMessage defaultMessage="Create group" />
            )}
          </Button>
        </>
      }
    >
      <Grid padding={majorScale(2)}>
        <Heading size={300} className="col-span-12">
          <FormattedMessage defaultMessage="Details" />
        </Heading>

        <FormField
          className="col-span-9"
          label={intl.formatMessage({ defaultMessage: 'Name' })}
          inline
        >
          <InputGroup
            placeholder={intl.formatMessage({
              defaultMessage: 'Group name',
            })}
            isInvalid={!!errors.name}
            validationMessage={errors.name && errors.name.message}
            ref={register({
              required: intl.formatMessage({
                defaultMessage: 'Please provide a name to the group',
              }),
              maxLength: {
                value: 100,
                message: intl.formatMessage({
                  defaultMessage: 'Name must be 100 characters or less',
                }),
              },
            })}
            name="name"
          />
        </FormField>

        <FormField
          className="col-span-9"
          label={intl.formatMessage({ defaultMessage: 'Description' })}
          inline
        >
          <TextArea
            placeholder={intl.formatMessage({
              defaultMessage: 'Group description',
            })}
            isInvalid={!!errors.description}
            validationMessage={errors.description && errors.description.message}
            ref={register({
              maxLength: {
                value: 300,
                message: intl.formatMessage({
                  defaultMessage: 'Description must be 300 characters or less',
                }),
              },
            })}
            name="description"
          />
        </FormField>
      </Grid>

      <Grid className="p-2 border-t-2 border-gray-100">
        <Heading size={300} className="col-span-12">
          <FormattedMessage defaultMessage="Pricing" />
        </Heading>

        <FormField
          className="col-span-9"
          label={intl.formatMessage({ defaultMessage: 'Default CMR' })}
          inline
        >
          <Controller
            control={control}
            name="default_cmr"
            render={({ onChange, value, onBlur }) => (
              <NumberInput
                numberStyle="percent"
                min={0}
                max={100}
                placeholder={intl.formatMessage({ defaultMessage: '%' })}
                ref={register({
                  required: intl.formatMessage({
                    defaultMessage:
                      'Please provide a default contribution margin ratio for the product group.',
                  }),
                })}
                value={value}
                onBlur={onBlur}
                onChange={(v) => onChange(v / 100)}
                minimumFractionDigits={0}
              />
            )}
          />
        </FormField>

        <FormField
          className="col-span-9"
          label={intl.formatMessage({ defaultMessage: 'Pricing matrix' })}
          inline
        >
          <div className="grid grid-cols-1 gap-y-2">
            {pricingMatrix.map((field, i) => (
              <ControlGroup key={field.id}>
                <EmptyInputGroup className="w-full">
                  {field.contact_group && (
                    <ContactGroupTitle contactGroupId={field.contact_group} />
                  )}
                  <input
                    name={`pricing[${i}].contact_group`}
                    ref={register()}
                    value={field.contact_group}
                    defaultValue={field.contact_group}
                    className="hidden"
                  />
                </EmptyInputGroup>
                <Controller
                  control={control}
                  rules={{
                    required: intl.formatMessage({
                      defaultMessage:
                        'Please provide a contribution margin ratio',
                    }),
                  }}
                  name={`pricing[${i}].cmr`}
                  render={({ onChange, value }) => (
                    <NumberInput
                      numberStyle="percent"
                      minimumFractionDigits={0}
                      maxWidth={80}
                      placeholder="%"
                      value={value}
                      defaultValue={value}
                      onChange={(v) => onChange(v / 100)}
                      isInvalid={
                        !!(
                          errors.pricing &&
                          errors.pricing[i] &&
                          errors.pricing[i]!.cmr
                        )
                      }
                    />
                  )}
                />
                <Button
                  className="flex"
                  iconLeft="trash"
                  onClick={() => remove(i)}
                />
              </ControlGroup>
            ))}
            <ContactGroupSelect
              filter={{
                filter: {
                  id__exclude: pricingMatrix.map(
                    (field) => field.contact_group
                  ),
                },
              }}
              onSelect={(group) => {
                append({
                  contact_group: group.id,
                  cmr: watch('default_cmr'),
                });
              }}
            >
              <Button iconLeft="add" height={34}>
                <FormattedMessage defaultMessage="Add customer group pricing" />
              </Button>
            </ContactGroupSelect>
          </div>
        </FormField>
      </Grid>
    </Dialog>
  );
};

export const ContactGroupTitle = observer(
  ({ contactGroupId }: { contactGroupId: number | string }) => {
    const { contactGroupStore } = useStores();
    const [contactGroup, setContactGroup] = useState<IContactGroup>();
    useEffect(() => {
      let isSub = true;
      contactGroupStore
        .getItemNaiveAsync(contactGroupId)
        .then((g) => isSub && setContactGroup(g));
      return () => {
        isSub = false;
      };
    }, [contactGroupId, contactGroupStore]);

    return contactGroup ? <>{contactGroup.name}</> : <Spinner size={16} />;
  }
);

export default ProductGroupDetailsDialog;
